import { createContext, useCallback, useContext, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

export type Entry = { id: string; name: string; sequence: string | null | undefined; url: string };
export type DraftSource = { discard?: () => void; busy?: boolean };
export type Workspace = {
  register: (entry: Entry) => void;
  dirty: { current: boolean };
  draftSources?: Set<DraftSource>;
  remove?: (id: string) => void;
  views: Map<string, unknown>;
  persist: () => void;
};
export const WorkspaceContext = createContext<Workspace | null>(null);
export function useSubmissionWorkspace() { return useContext(WorkspaceContext); }

/** Per-submission UI state, never document data, drafts or permissions. */
export function useSubmissionViewState<T>(id: string, field: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const workspace = useSubmissionWorkspace();
  const key = `${id}:${field}`;
  const [value, setValue] = useState<T>(() => workspace?.views.has(key) ? workspace.views.get(key) as T : initial);
  const update = useCallback<Dispatch<SetStateAction<T>>>((next) => {
    setValue((previous) => {
      const resolved = typeof next === 'function' ? (next as (old: T) => T)(previous) : next;
      workspace?.views.set(key, resolved);
      workspace?.persist();
      return resolved;
    });
  }, [key, workspace]);
  return [value, update];
}

/** Independent editors must not clear another editor's unsaved state. */
export function useSubmissionDraft(dirty: boolean, discard?: () => void, busy = false): void {
  const workspace = useSubmissionWorkspace();
  const source = useRef<DraftSource>({});
  useEffect(() => {
    const token = source.current;
    token.busy = busy;
    if (discard) token.discard = discard;
    else delete token.discard;
    const sources = workspace?.draftSources;
    if (dirty || busy) sources?.add(token);
    return () => { sources?.delete(token); };
  }, [workspace, dirty, discard, busy]);
}
